
import React, { createContext, useContext, useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useSalEngine } from '../hooks/useSalEngine';
import { DEFAULT_CONFIG } from '../constants';
import { Order, SystemConfig, AppMode } from '../types';
import { supabase } from '../services/supabase';
import { toast } from 'react-hot-toast';
import { useHubExitDetection } from '../hooks/useHubExitDetection';
import { DistanceService } from '../services/parserService';
import { enrichOrderWithLocation } from '../services/reconService';

type SalEngineType = ReturnType<typeof useSalEngine>;

interface SalContextType extends SalEngineType {
  safeConfig: any;
  isAdmin: boolean;
  handleLogout: () => Promise<void>;
  handleLocationUpdate: (did: string, lat: number, lng: number) => void;
  jarvisActions: any;
  isRecoveryMode: boolean;
  setIsRecoveryMode: (val: boolean) => void;
}

const SalContext = createContext<SalContextType | null>(null);

export const SalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const engine = useSalEngine();
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);

  const {
    userProfile, appMode, setAppMode,
    currentDriverId, setCurrentDriverId,
    orders, setOrders, setDrivers,
    setViewMode, setFocusedOrderId,
    setIsCommandBarOpen, setContext, actions, storeId,
    dispatcherTheme, driverTheme
  } = engine;

  const activeTheme = appMode === AppMode.DISPATCHER ? dispatcherTheme : driverTheme;

  const safeConfig = useMemo(() => {
    if (!engine.config) return DEFAULT_CONFIG;
    return {
      ...DEFAULT_CONFIG,
      ...engine.config,
      cardapioWeb: { ...DEFAULT_CONFIG.cardapioWeb, ...engine.config.cardapioWeb },
      openDelivery: { ...DEFAULT_CONFIG.openDelivery, ...engine.config.openDelivery },
      oracle: { ...DEFAULT_CONFIG.oracle, ...engine.config.oracle },
      communication: { ...DEFAULT_CONFIG.communication, ...engine.config.communication },
      isSolarMode: activeTheme === 'solar'
    };
  }, [engine.config, activeTheme]);

  const telemetry = useHubExitDetection({ config: safeConfig, setOrders });
  const lastDriverPos = useRef<Record<string, { lat: number, lng: number, ts: number }>>({});

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      setAppMode(AppMode.DISPATCHER);
      setCurrentDriverId(null);
      sessionStorage.removeItem('sal_last_session_ts');
      sessionStorage.removeItem('sal_current_driver_id');
      localStorage.removeItem('sal_last_session_ts');
      localStorage.removeItem('sal_current_driver_id');
      toast.success("Sessão encerrada…");
    } catch (error) { toast.error("Erro ao sair."); }
  };

  const handleLocationUpdate = useCallback((did: string, lat: number, lng: number) => {
    if (userProfile?.role !== 'driver' && appMode === AppMode.DISPATCHER) return;

    const now = Date.now();
    const last = lastDriverPos.current[did];
    const hasActiveRoute = orders.some((order) =>
      order.driverId === did && (order.status === 'assigned' || order.status === 'dispatched' || !!order.pickupConfirmed)
    );

    const minDistanceKm = hasActiveRoute ? 0.04 : 0.25;
    const minIntervalMs = hasActiveRoute ? 20000 : 120000;

    if (last) {
      const movedKm = DistanceService.getDistanceSync(last.lat, last.lng, lat, lng);
      const elapsedMs = now - last.ts;
      if (movedKm < minDistanceKm && elapsedMs < minIntervalMs) return;
    }

    lastDriverPos.current[did] = { lat, lng, ts: now };
    setDrivers((prev: any[]) => {
      const idx = prev.findIndex((d: any) => d.id === did);
      if (idx < 0) return prev;

      const target = prev[idx];
      if (target.lat === lat && target.lng === lng) return prev;

      const next = [...prev];
      next[idx] = {
        ...target,
        lat,
        lng,
        currentLat: lat,
        currentLng: lng,
        lastUpdate: now
      };
      return next;
    });

    if (appMode === AppMode.DRIVER_DASHBOARD) telemetry.handleHubExitTelemetry(did, lat, lng, orders);
  }, [setDrivers, telemetry, orders, appMode, userProfile]);

  useEffect(() => {
    const checkRecovery = () => {
      const hash = window.location.hash || '';
      const search = window.location.search || '';
      setIsRecoveryMode(hash.includes('type=recovery') || search.includes('type=recovery'));
    };

    checkRecovery();
    window.addEventListener('hashchange', checkRecovery);
    window.addEventListener('popstate', checkRecovery);

    const { data: authListener } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setIsRecoveryMode(true);
    });

    return () => {
      window.removeEventListener('hashchange', checkRecovery);
      window.removeEventListener('popstate', checkRecovery);
      authListener?.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (!engine.isAuthenticated) return;
      if (userProfile?.role === 'driver') return;
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsCommandBarOpen(true);
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [setIsCommandBarOpen, userProfile, engine.isAuthenticated]);

  useEffect(() => {
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (safeConfig.isSolarMode) {
      // Adicionar classe solar-mode ao HTML para que o Tailwind e CSS respondam
      document.documentElement.classList.add('solar-mode');
      document.documentElement.classList.remove('dark');
      document.documentElement.setAttribute('color-scheme', 'light');
      if (themeColorMeta) themeColorMeta.setAttribute('content', '#f8fafc');
    } else {
      document.documentElement.classList.remove('solar-mode');
      document.documentElement.classList.add('dark');
      document.documentElement.setAttribute('color-scheme', 'dark');
      if (themeColorMeta) themeColorMeta.setAttribute('content', '#1e293b');
    }
  }, [safeConfig.isSolarMode]);

  const isAdmin = userProfile?.role === 'admin';

  // v59.0: Função para geocodificar pedidos sem coordenadas
  const refreshMissingCoordinates = useCallback(async (ordersToRefresh?: Order[]) => {
    const targetOrders = ordersToRefresh || orders;
    const ordersWithoutCoords = targetOrders.filter(o => 
      o.type === 'DELIVERY' && (!o.lat || !o.lng)
    );

    if (ordersWithoutCoords.length === 0) {
      toast.success("Todos os pedidos já possuem coordenadas!");
      return;
    }

    toast.loading(`Geocodificando ${ordersWithoutCoords.length} pedidos...`, { id: 'geo-loading' });

    const config = safeConfig;
    const enrichedOrders: Order[] = [];

    for (const order of ordersWithoutCoords) {
      try {
        const enriched = await enrichOrderWithLocation(order, {
          lat: config.businessLat,
          lng: config.businessLng,
          googleMapsApiKey: config.googleMapsApiKey,
          hereMapsApiKey: config.hereMapsApiKey,
          storeId: storeId || '',
          storeCity: config.businessAddress
        });
        enrichedOrders.push(enriched);
      } catch (e) {
        console.warn(`[SAL-RECON] Falha ao geocodificar pedido ${order.id}:`, e);
      }
    }

    if (enrichedOrders.length > 0) {
      setOrders(prev => {
        const orderMap = new Map(enrichedOrders.map(o => [o.id, o]));
        return prev.map(o => orderMap.get(o.id) || o);
      });
      toast.success(`${enrichedOrders.length} coordenadas atualizadas!`, { id: 'geo-loading' });
    } else {
      toast.error("Nenhuma coordenada obtida.", { id: 'geo-loading' });
    }
  }, [orders, safeConfig, storeId, setOrders]);

  const jarvisActions = useMemo(() => ({
    setRainMode: (mode: 'off' | 'light' | 'heavy') => setContext(prev => ({ ...prev, rainMode: mode })),
    toggleTraffic: () => setContext(prev => ({ ...prev, isHighTraffic: !prev.isHighTraffic })),
    focusOrder: setFocusedOrderId,
    focusDriver: (_id: string) => undefined,
    openSettings: () => isAdmin ? actions.toggleModal('settings', true) : toast.error("Ação restrita ao Admin."),
    setViewMode,
    simulateOrder: actions.handleSimulateWebhook,
    refreshMissingCoordinates
  }), [setContext, setFocusedOrderId, isAdmin, actions, setViewMode, refreshMissingCoordinates]);

  const value = {
    ...engine,
    safeConfig,
    isAdmin,
    handleLogout,
    handleLocationUpdate,
    jarvisActions,
    isRecoveryMode,
    setIsRecoveryMode
  };

  return <SalContext.Provider value={value}>{children}</SalContext.Provider>;
};

export const useSal = () => {
  const context = useContext(SalContext);
  if (!context) throw new Error('useSal must be used within a SalProvider');
  return context;
};
